import { useState } from "react";
import styled, { css } from "styled-components";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { MediaType, getSpaceMediaType, getSpaceThumbnail } from "@lifeat/shared/spaces/SpacesUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtTooltip from "@common/LifeAtTooltip/LifeAtTooltip";
import CloseIcon from "@icons/CloseIcon.svg";
import DragIcon from "@icons/DragIndicatorIcon.svg";
import Styles from "@themes/Styles";
import { customProperties } from "@util/StyleUtil";
import { SelectSavedSpaceEvent } from "../../../../../AmplitudeSpaceEvents";

const REMOVED_SPACE_TOOLTIP_LABEL = "⚠️ This space has been removed.";
const REMOVED_SPACE_TOOLTIP_INFO = "Click the × to delete.";

const CREATE_ACCOUNT_TO_ACCESS_SPACE_TITLE = "Please create a free account to access your saved space.";

const savedSpaceElPaddingMargin = "8px";

const MediaTypeLabel = {
    [MediaType.VIDEO]: {
        text: "VID",
        background: "#a82fb5",
    },
    [MediaType.IMAGE]: {
        text: "IMG",
        background: "#1072c7",
    },
} as const;

const StyledDragIcon = styled(DragIcon)``;

const StyledCloseIcon = styled(CloseIcon)`
    fill: ${({ theme }) => theme.iconColor};
`;

const Root = styled.div<{ draggable: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    ${({ draggable }) =>
        draggable &&
        css`
            // Unset the margin because we set it in LifeAtPopupMenu.scss for the
            // draggable div container (to apply the margin to the draggable components
            // instead of saved spaces (due to a rendering delay for the margin when you drag and drop).
            margin-bottom: unset;

            &:hover {
                ${StyledDragIcon} {
                    transition: all 0.2s ease;
                    margin-right: 2 * ${savedSpaceElPaddingMargin};
                    fill: ${({ theme }) => theme.iconColor};
                }
            }
        `}
`;

const Container = styled.a<{ disabled: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: start;
    align-items: center;
    gap: ${savedSpaceElPaddingMargin};

    width: 280px;

    padding: ${savedSpaceElPaddingMargin};
    margin-right: ${savedSpaceElPaddingMargin};

    box-sizing: border-box;
    border-radius: ${Styles.borderRadiusSharp};

    cursor: pointer;
    font-size: 12px;

    border: 1px solid ${({ theme }) => theme.borderPrimary};
    background: ${({ theme }) => theme.backgroundTransparent};

    &:hover {
        background: ${({ theme }) => theme.backgroundSecondaryTransparent};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 35%;
            cursor: default;
        `}
`;

const Thumbnail = styled.img`
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: ${Styles.borderRadiusExtraSharp};
`;

const SpaceName = styled.span`
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const MediaTypePill = styled.div`
    border-radius: 100px;
    width: 40px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    background: var(--background);
    color: white;
`;

const UnsaveSpace = styled.a`
    opacity: 50%;
    cursor: pointer;
`;

interface SavedSpaceElementProps {
    readonly savedSpace: VirtualSpace;
    readonly switchToSpace: (space: VirtualSpace) => void;
    readonly unsaveSpace: (space: VirtualSpace) => void;
    /**
     * Allows drag-and-drop reordering of this component (not enabled for users without account).
     */
    readonly enableReorder: boolean;
    readonly isSpaceRemoved?: boolean;
    readonly exceedsFreeSaveCount?: boolean;
    /**
     * Open state of the menu to handle closing menu when space is clicked
     */
    readonly setSavedSpacesOpen?: (closed: boolean) => void;
}
export default function SavedSpaceElement(props: SavedSpaceElementProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [mouseCoords, setMouseCoords] = useState({ top: 0, left: 0 });

    const handleSavedSpaceClick = () => {
        if (!props.isSpaceRemoved && !props.exceedsFreeSaveCount) {
            const space = props.savedSpace;
            props.switchToSpace(space);

            // close saved spaces pop up menu
            if (props.setSavedSpacesOpen) props.setSavedSpacesOpen(false);

            // Log Events
            GA4Service.logSpaceEvent(GA4Event.SELECT_SAVED_SPACE, {
                [GA4EventProperties.space_info_keys.id]: space.id,
                [GA4EventProperties.space_info_keys.name]: space.name,
                [GA4EventProperties.space_info_keys.category]: space.category,
                [GA4EventProperties.space_creator_name.key]: space.creatorName,
            });
            amplitudeService.logEvent(
                new SelectSavedSpaceEvent({
                    spaceId: space.id,
                    spaceName: space.name,
                    spaceCategory: space.category,
                    spaceCreator: space.creatorName,
                }),
            );
        } else {
            console.warn(`This space no longer exists: ${JSON.stringify(props.savedSpace)}`);
        }
    };

    let tooltipTitle: string | null = null;
    let tooltipMessage: string | null = null;
    if (props.isSpaceRemoved) {
        tooltipTitle = REMOVED_SPACE_TOOLTIP_LABEL;
        tooltipMessage = REMOVED_SPACE_TOOLTIP_INFO;
    } else if (props.exceedsFreeSaveCount) {
        tooltipTitle = "";
        tooltipMessage = CREATE_ACCOUNT_TO_ACCESS_SPACE_TITLE;
    }

    const mediaType = getSpaceMediaType(props.savedSpace);

    return (
        <Root draggable={props.enableReorder}>
            {props.enableReorder && <StyledDragIcon />}

            <LifeAtTooltip
                showTooltip={showTooltip && (props.isSpaceRemoved || props.exceedsFreeSaveCount)}
                title={tooltipTitle}
                message={tooltipMessage}
                mouseCoords={mouseCoords}
            />

            <Container
                disabled={props.isSpaceRemoved || props.exceedsFreeSaveCount}
                onClick={handleSavedSpaceClick}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onMouseMove={(e) => setMouseCoords({ left: e.clientX + 12, top: e.clientY + 12 })}
            >
                <Thumbnail src={getSpaceThumbnail(props.savedSpace.videoUrl)} />
                <SpaceName>{props.savedSpace.name}</SpaceName>
                <MediaTypePill style={customProperties({ "--background": MediaTypeLabel[mediaType].background })}>
                    {MediaTypeLabel[mediaType].text}
                </MediaTypePill>
            </Container>
            <UnsaveSpace onClick={() => props.unsaveSpace(props.savedSpace)}>
                <StyledCloseIcon />
            </UnsaveSpace>
        </Root>
    );
}
